import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';

// Lands here after Google OAuth as /auth-callback#session_id=...
// Exchanges the session_id for our JWT, then routes into the app.
const AuthCallbackPage = () => {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError(true);
      toast.error('Sign-in link was invalid. Please try again.');
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        await googleSession(sessionId);
        // Clear the session_id from the URL so it isn't reused.
        window.history.replaceState(null, '', '/auth-callback');
        toast.success('Signed in with Google!');
        navigate('/creator-dashboard', { replace: true });
      } catch (err) {
        setError(true);
        toast.error(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
        navigate('/login', { replace: true });
      }
    })();
  }, [googleSession, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4" data-testid="auth-callback">
      <Logo size="lg" />
      {!error && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Signing you in…</span>
        </div>
      )}
    </div>
  );
};

export default AuthCallbackPage;
